import { add, angleDelta, clamp, dot, headingVector, length, rightVector, scale, subtract } from './math'
import { getRacerIdentity } from './racer-config'
import { getTrackSample, projectToTrack } from './track'
import type { AiProfile, AiWorldState, ControlInput, Track, VehicleState } from './types'

function createAiProfile(id: string, driving: Omit<AiProfile, 'id' | 'name' | 'accent'>): AiProfile {
  return { ...getRacerIdentity(id), ...driving }
}

export const AI_PROFILES: readonly AiProfile[] = [
  createAiProfile('rival-1', {
    preferredSpeed: 53,
    aggression: 0.82,
    driftTendency: 0.88,
    lineOffset: -1.6,
  }),
  createAiProfile('rival-2', {
    preferredSpeed: 51,
    aggression: 0.66,
    driftTendency: 0.72,
    lineOffset: 1.4,
  }),
  createAiProfile('rival-3', {
    preferredSpeed: 50,
    aggression: 0.48,
    driftTendency: 0.62,
    lineOffset: -0.4,
  }),
  createAiProfile('rival-4', {
    preferredSpeed: 49,
    aggression: 0.74,
    driftTendency: 0.54,
    lineOffset: 2.2,
  }),
  createAiProfile('rival-5', {
    preferredSpeed: 52,
    aggression: 0.58,
    driftTendency: 0.8,
    lineOffset: -2.2,
  }),
]

function tangentHeading(vehicle: VehicleState): number {
  return Math.atan2(vehicle.velocity.x, vehicle.velocity.y)
}

export function getTrackCurvature(track: Track, sampleIndex: number): number {
  const current = getTrackSample(track, sampleIndex)
  const future = getTrackSample(track, sampleIndex + 14)
  const currentHeading = Math.atan2(current.tangent.x, current.tangent.y)
  const futureHeading = Math.atan2(future.tangent.x, future.tangent.y)
  return Math.abs(angleDelta(currentHeading, futureHeading))
}

function shouldFire(vehicle: VehicleState, world: AiWorldState, profile: AiProfile): boolean {
  if (
    profile.aggression < 0.45 ||
    vehicle.weaponCooldown > 0 ||
    vehicle.fireSuppressionTimer > 0 ||
    vehicle.reloadTimer > 0 ||
    vehicle.driverStunTimer > 0 ||
    vehicle.ammo <= 0
  ) {
    return false
  }
  const forward = headingVector(vehicle.heading)
  const right = rightVector(vehicle.heading)
  return world.vehicles.some((target) => {
    if (target.id === vehicle.id) return false
    const offset = subtract(target.position, vehicle.position)
    const range = length(offset)
    return range > 3 && range < 34 && dot(offset, forward) > 0 && Math.abs(dot(offset, right)) < 3.7
  })
}

export function getAiInput(vehicle: VehicleState, world: AiWorldState, track: Track, profile: AiProfile): ControlInput {
  const projection = projectToTrack(track, vehicle.position, vehicle.trackSampleIndex)
  const roadEdge = Math.abs(projection.lateral) / track.width
  const lookAhead = 8 + Math.round(Math.abs(vehicle.speed) * 0.18)
  const targetSample = getTrackSample(track, projection.sampleIndex + lookAhead)
  const racingLineOffset = roadEdge > 0.58 ? 0 : profile.lineOffset
  const target = add(targetSample.position, scale(targetSample.normal, racingLineOffset))
  const toTarget = subtract(target, vehicle.position)
  const targetHeading = Math.atan2(toTarget.x, toTarget.y)
  const headingError = angleDelta(vehicle.heading, targetHeading)
  const steer = clamp(headingError * 2.45 + (projection.lateral / track.width) * 0.9, -1, 1)
  const curvature = Math.max(
    getTrackCurvature(track, projection.sampleIndex),
    getTrackCurvature(track, projection.sampleIndex + Math.round(lookAhead * 0.4)),
  )
  const targetSpeed = clamp(
    profile.preferredSpeed - curvature * 48 - Math.max(0, roadEdge - 0.45) * 28,
    20,
    profile.preferredSpeed,
  )
  const brake = clamp((Math.abs(vehicle.speed) - targetSpeed) / 11, 0, 1)
  const throttle = brake > 0.08 ? 0.15 : clamp(0.55 + (targetSpeed - Math.abs(vehicle.speed)) / 14, 0.25, 1)
  const aligned = roadEdge < 0.4 && Math.abs(headingError) < 0.12 && Math.abs(projection.lateral - racingLineOffset) < 3
  const drift =
    roadEdge < 0.55 &&
    curvature > 0.25 &&
    Math.abs(vehicle.speed) > 22 &&
    Math.abs(steer) > 0.14 &&
    profile.driftTendency >= 0.5
  const boost = curvature < 0.14 && aligned && vehicle.boost > 20 && Math.abs(vehicle.speed) < targetSpeed + 9
  const fire = shouldFire(vehicle, world, profile)
  const reload =
    vehicle.reloadTimer <= 0 &&
    vehicle.driverStunTimer <= 0 &&
    (vehicle.ammo <= 0 || (vehicle.ammo <= 4 && !fire && curvature < 0.22))

  return {
    throttle,
    brake,
    steer,
    drift,
    boost,
    fire,
    reload,
  }
}

export function getAiProfile(vehicleId: string): AiProfile {
  return AI_PROFILES.find((profile) => profile.id === vehicleId) ?? AI_PROFILES[0]
}

export function getVelocityHeading(vehicle: VehicleState): number {
  return length(vehicle.velocity) > 0.1 ? tangentHeading(vehicle) : vehicle.heading
}
